import axios from "axios";
import { IHeaders } from "../interfaces/IHeaders";
import { CONFIG } from "./config";

const BASE = `${CONFIG.API_URL}bodyshell`;

// ─── Response types ───────────────────────────────────────────────────────────

export interface IBodyShellListItem {
  id: number;
  name: string;
  scale: number;
  active: boolean;
  filledSlots: number;
  totalSlots: number;
  assocCount: number;
  vehicleAssoc: string;
}

export interface IBodyShellVehicleAssoc {
  vehicleId: number;
  vehicleName: string;
  countryCode: string;
}

export interface IBodyShellDetail {
  id: number;
  name: string;
  scale: number;
  active: boolean;
  carPath: string;
  frontLeftCPath: string;
  frontLeftPath: string;
  frontRightCPath: string;
  frontRightPath: string;
  rearLeftCPath: string;
  rearLeftPath: string;
  rearRightCPath: string;
  rearRightPath: string;
  frontLeftCOwPath: string;
  frontRightCOwPath: string;
  rearLeftCOwPath: string;
  rearRightCOwPath: string;
  trunkPath: string;
  hoodPath: string;
  sunRoofCPath: string;
  sunRoofPath: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  vehicles: IBodyShellVehicleAssoc[];
}

export interface IBodyShellVehicleOption {
  id: number;
  name: string;
  countryCode: string;
  backgroundColor: string;
  teaserMediaId: string;
  imageName: string;
  status: string;
}

// ─── Request type ─────────────────────────────────────────────────────────────

export interface IBodyShellRequest {
  name: string;
  scale: number;
  active?: boolean;
  vehicleId?: number;
  carImageBase64: string;
  frontLeftCImageBase64: string;
  frontLeftImageBase64: string;
  frontRightCImageBase64: string;
  frontRightImageBase64: string;
  rearLeftCImageBase64: string;
  rearLeftImageBase64: string;
  rearRightCImageBase64: string;
  rearRightImageBase64: string;
  frontLeftCOwImageBase64: string;
  frontRightCOwImageBase64: string;
  rearLeftCOwImageBase64: string;
  rearRightCOwImageBase64: string;
  trunkImageBase64: string;
  hoodImageBase64: string;
  sunRoofCImageBase64?: string;
  sunRoofImageBase64?: string;
}

export type IBodyShellImageSlot =
  | "car"
  | "frontLeftC"
  | "frontLeft"
  | "frontRightC"
  | "frontRight"
  | "rearLeftC"
  | "rearLeft"
  | "rearRightC"
  | "rearRight"
  | "frontLeftCOw"
  | "frontRightCOw"
  | "rearLeftCOw"
  | "rearRightCOw"
  | "trunk"
  | "hood"
  | "sunRoofC"
  | "sunRoof";

// ─── List params ──────────────────────────────────────────────────────────────

export interface IBodyShellListParams {
  active?: boolean;
  searchText?: string;
  page?: number;
  size?: number;
  sort?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const bodyshellService = {
  /** Lista body shells de forma paginada com filtros opcionais (active, searchText, page, size, sort). */
  list: (params: IBodyShellListParams, headers: IHeaders) =>
    axios.get<{
      content: IBodyShellListItem[];
      totalElements: number;
      totalPages: number;
      size: number;
      number: number;
    }>(BASE, { ...headers, params }),

  /** Retorna os detalhes completos de um body shell, incluindo todos os paths de imagem e veículos associados. */
  getById: (id: number, headers: IHeaders) =>
    axios.get<IBodyShellDetail>(`${BASE}/${id}`, headers),

  /** Lista veículos disponíveis para associação — apenas os que ainda não possuem body shell vinculado. Aceita busca textual opcional via `param`. */
  getVehicles: (headers: IHeaders, param?: string) =>
    axios.get<IBodyShellVehicleOption[]>(`${BASE}/vehicles`, {
      ...headers,
      params: param ? { param } : undefined,
    }),

  /** Verifica unicidade do nome. Retorna `true` se já existir um body shell com esse nome exato, `false` caso contrário. */
  checkName: (name: string, headers: IHeaders) =>
    axios.get<boolean>(`${BASE}/check-name`, {
      ...headers,
      params: { param: name },
    }),

  /** Cria um novo body shell com upload de até 17 imagens em base64. Valida unicidade do nome (409 em duplicata) e associa um veículo se `vehicleId` for informado. */
  create: (data: IBodyShellRequest, headers: IHeaders) =>
    axios.post<IBodyShellDetail>(BASE, data, headers),

  /** Atualiza um body shell existente re-enviando todas as imagens. Valida unicidade do nome ignorando o próprio registro. */
  update: (id: number, data: IBodyShellRequest, headers: IHeaders) =>
    axios.put<IBodyShellDetail>(`${BASE}/${id}`, data, headers),

  /** Remove um body shell. Retorna 409 se houver veículos associados. */
  remove: (id: number, headers: IHeaders) =>
    axios.delete(`${BASE}/${id}`, headers),

  /** Atualiza a imagem de um slot específico sem precisar reenviar o body shell completo. */
  updateImage: (
    id: number,
    slot: IBodyShellImageSlot,
    imageBase64: string,
    headers: IHeaders,
  ) =>
    axios.put<IBodyShellDetail>(
      `${BASE}/${id}/images/${slot}`,
      { imageBase64 },
      headers,
    ),

  /** Associa um veículo ao body shell. Remove associações anteriores e define o novo veículo. Retorna 409 se o veículo já estiver associado. */
  associateVehicle: (id: number, vehicleId: number, headers: IHeaders) =>
    axios.post(`${BASE}/${id}/vehicles/${vehicleId}`, {}, headers),

  /** Desassocia um veículo do body shell. Retorna 404 se o veículo não estiver associado. */
  dissociateVehicle: (id: number, vehicleId: number, headers: IHeaders) =>
    axios.delete(`${BASE}/${id}/vehicles/${vehicleId}`, headers),
};
